import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import {
  IcTruckLoaderCrane,
  IcTruckTypeConstruction,
  IcTruckTypeDoubleContainer,
  IcTruckTypeLiquidWaste,
  IcTruckTypeMsw,
  IcTruckTypeTipper,
} from "@racoon/icons";
import { cn } from "@racoon/ui";
import type { ComponentType, SVGProps } from "react";

import { FleetOnLine, ShareTone, shareTone } from "~/shared/lib/pickup-status/fleet-on-line";

type Icon = ComponentType<Omit<SVGProps<SVGSVGElement>, "ref">>;

// The crane exists only as a 56x56 map icon; crop it to the 50x24 card icons' frame.
const IcTruckTypeLoaderCrane: Icon = (props) => (
  <IcTruckLoaderCrane viewBox="1.5 15.75 50 24" {...props} />
);

const T = ConnectProto.TruckType;
const TYPE_ICON: Partial<Record<ConnectProto.TruckType, Icon>> = {
  [T.MSW]: IcTruckTypeMsw,
  [T.CONSTRUCTION]: IcTruckTypeConstruction,
  [T.DOUBLE_CONTAINER]: IcTruckTypeDoubleContainer,
  [T.TIPPER]: IcTruckTypeTipper,
  [T.LIQUID_WASTE]: IcTruckTypeLiquidWaste,
  [T.LOADER_CRANE]: IcTruckTypeLoaderCrane,
};

const TONE_CLASS: Record<ShareTone, string> = {
  good: "bg-green-100 text-green-600",
  fair: "bg-yellow-100 text-yellow-600",
  low: "bg-orange-100 text-orange-600",
  poor: "bg-red-100 text-red-600",
};
const SHARE_CLASS = "bg-slate-200 text-text-subtle";

function pct(value: number, total: number) {
  return total > 0 ? (value / total) * 100 : 0;
}

export function FleetOnLineSection({
  fleet,
  volume,
  weightT,
  fmt,
}: {
  fleet: FleetOnLine;
  /** The day's collected totals, which the per-type shares are of. */
  volume: number;
  weightT: number;
  fmt: Intl.NumberFormat;
}) {
  const t = useT();
  const k = (key: "fleetOnLine" | "onLine" | "volume" | "weight" | "m3" | "tonnes"): string =>
    t(Namespace.Routes, `pickupStatusTab.${key}`);
  const of = (onLine: number, total: number) =>
    t(Namespace.Routes, "pickupStatusTab.onLineOf", { onLine, total });
  const m3 = `${k("m3")}`;
  const tonnes = `${k("tonnes")}`;
  const onLinePct = pct(fleet.onLine, fleet.total);

  return (
    <div className="flex border-t">
      <div className="flex w-[clamp(200px,22%,296px)] shrink-0 flex-col gap-4 border-r p-6">
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-bold uppercase text-text-body">{k("fleetOnLine")}</h3>
          <span
            className={cn(
              "flex h-8 items-center whitespace-nowrap rounded-full px-3 text-xl font-bold",
              TONE_CLASS[shareTone(onLinePct)],
            )}
          >
            {fmt.format(onLinePct)}%
          </span>
        </div>
        <dl className="flex flex-col gap-3">
          {(
            [
              [k("onLine"), of(fleet.onLine, fleet.total)],
              [k("volume"), `${fmt.format(volume)} ${m3}`],
              [k("weight"), `${fmt.format(weightT)} ${tonnes}`],
            ] as const
          ).map(([label, value]) => (
            <div
              key={label}
              className="flex min-h-5 flex-wrap items-center justify-between gap-x-2 gap-y-1"
            >
              <dt className="text-xs font-bold uppercase text-text-subtle">{label}</dt>
              <dd className="whitespace-nowrap text-sm font-medium tracking-[-0.2px] text-text-body">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {fleet.types.map((line) => {
        const Icon = TYPE_ICON[line.type];
        const rows: [string, number, string][] = [
          [of(line.onLine, line.total), pct(line.onLine, line.total), ""],
          [`${fmt.format(line.volume)} ${m3}`, pct(line.volume, volume), SHARE_CLASS],
          [`${fmt.format(line.weightT)} ${tonnes}`, pct(line.weightT, weightT), SHARE_CLASS],
        ];
        return (
          <div
            key={line.type}
            className="flex min-w-0 flex-1 basis-0 flex-col gap-6 border-r px-[clamp(12px,1.8cqw,24px)] py-6 last:border-r-0"
          >
            <div className="flex min-h-6 items-center justify-between gap-2">
              <h3 className="truncate text-xs font-bold uppercase text-text-body">
                {t(
                  Namespace.Routes,
                  `ganttTruckTypes.${ConnectProto.TruckType[line.type] as keyof typeof ConnectProto.TruckType}`,
                )}
              </h3>
              {Icon && <Icon aria-hidden className="h-6 w-[50px] shrink-0" />}
            </div>
            <div className="flex flex-col gap-3">
              {rows.map(([value, share, badgeClass], i) => (
                <div
                  key={i}
                  className="flex min-h-5 flex-wrap items-center justify-between gap-x-2 gap-y-1"
                >
                  <span className="whitespace-nowrap text-sm font-medium tracking-[-0.2px] text-text-body">
                    {value}
                  </span>
                  <span
                    className={cn(
                      "flex h-5 w-[50px] shrink-0 items-center justify-center rounded-full text-xs font-bold",
                      badgeClass || TONE_CLASS[shareTone(share)],
                    )}
                  >
                    {fmt.format(share)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
